import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAsync } from 'react-use'
import { useMessaging } from '@ucc/admin-messaging'
import { JIVE_PRODUCT_ABRS } from '@ucc/unified-admin-common'
import mixpanel from 'mixpanel-browser'

import api from 'api'
import { FeatureFlagContext, OrganizationContext, RegionsContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import { BillingInfo } from 'types/Billing'
import { CopilotLegalTerms, CopilotLegalTermsStatus } from 'types/CopilotLegalTerms'
import { DynamicSetupConfig } from 'types/DynamicSetupConfig'
import { GuidedSetupConfig } from 'types/GuidedSetupConfig'
import { ProductMigrationDetails } from 'types/MigrationsDetails'
import { PBX } from 'types/Pbx'
import { DEFAULT_PBX_SMS_CAMPAIGN_DETAILS } from 'types/PbxSmsCampaignDetails'
import { DEFAULT_PBX_PRODUCTS_EXPLORATION } from 'types/ProductsExploration'
import { Subscription } from 'types/Subscription'
import {
  fetchAdminCopilotLegalTerms,
  fetchDynamicSetupConfig,
  fetchGuidedSetupConfig,
  fetchPbx,
  fetchPbxSmsCampaignDetails,
  fetchProductsExploration,
  fetchWithToken,
  getBillingInfo,
  getProductMigrationDetails,
} from 'utils'
import { CHANNEL_JIVE, FEATURE_FLAGS } from 'utils/constants'
import { Account, default as fetchAccount } from 'utils/fetchAccount'
import { fetchUsers } from 'utils/fetchUsers'
import getUserCountForMixpanel from 'utils/getUserCountForMixpanel'
import { BILLING_INFO_UPDATE_EVENT } from 'utils/messaging/events'
import { storeLicensesUsage, storePbxInfoForQualtrics } from 'utils/qualtricsSurvey'
import { getSubscriptions } from 'utils/ucs/getSubscriptions'

interface OrganizationProviderProps {
  children?: ReactNode
}

const OrganizationProvider: FC<OrganizationProviderProps> = ({ children }) => {
  const { orgId: selectedPbxId, accountKey, productsSettings, licensesDetails } = useAccountContext()
  const [currentPbx, setCurrentPbx] = useState<PBX | null>(null)

  const [guidedSetupConfig, setGuidedSetupConfig] = useState<GuidedSetupConfig | undefined>(undefined)
  const [dynamicSetupConfig, setDynamicSetupConfig] = useState<DynamicSetupConfig | undefined>(undefined)
  const [pbxSmsCampaignDetails, setPbxSmsCampaignDetails] = useState(DEFAULT_PBX_SMS_CAMPAIGN_DETAILS)
  const [productsExploration, setProductsExploration] = useState(DEFAULT_PBX_PRODUCTS_EXPLORATION)
  const [copilotLegalTerms, setCopilotLegalTerms] = useState<CopilotLegalTerms | undefined>(undefined)
  const [showSwitchBackDialog, setShowSwitchBackDialog] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [userAccount, setUserAccount] = useState<Account | null>(null)
  const [billingInfo, setBillingInfo] = useState<BillingInfo | undefined>(undefined)
  const [productMigrationDetails, setProductMigrationDetails] = useState<ProductMigrationDetails | undefined>(undefined)
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const features = useContext(FeatureFlagContext)
  const regions = useContext(RegionsContext)
  const templatableProduct = ['GOTORESOLVE', 'G2ARS', 'G2T', 'G2W', 'G2M', ...JIVE_PRODUCT_ABRS]
  const dynamicSetupFeature = useMemo(() => {
    return features?.find((f) => f.feature === FEATURE_FLAGS.useDynamicSetupConfig)
  }, [features])

  const isJiveProductsAvailable = (licenses) =>
    !!licenses?.results?.some((license) => JIVE_PRODUCT_ABRS.some((product) => license.products?.includes(product)))

  const handleFetchPBX = useCallback(async (selectedPbxId) => {
    const pbx = await fetchPbx(selectedPbxId)
    mixpanel.push(['register', { pbxAccountType: pbx.type }])
    storePbxInfoForQualtrics(pbx)
    setCurrentPbx({ ...pbx })
  }, [])

  const handleUpgrade = useCallback((pbxId: string) => {
    return fetchWithToken({
      method: 'PUT',
      resource: api.pbx,
      path: `pbxs/${pbxId}/unified-admin`,
      filters: 'isUnifiedAdmin=true',
    })
      .then((response: Response) => {
        setCurrentPbx((pbx) => ({ ...pbx, unifiedAdmin: true }))
        return response.ok
      })
      .catch((err: Error) => {
        console.error(err.toString())
      })
  }, [])

  const onCopilotLegalTermsAgreements = useCallback(
    async (legalTermsStatus: CopilotLegalTermsStatus) => {
      if (accountKey && copilotLegalTerms) {
        setCopilotLegalTerms({ ...copilotLegalTerms, legalTermsStatus })
      }
    },
    [accountKey, copilotLegalTerms],
  )

  const supportVirtualFax = useCallback(
    (currentPbx: PBX | null): boolean => {
      const regionSupportFax =
        currentPbx && currentPbx.region && regions.find((r) => r.code === currentPbx.region)?.faxSupported

      return (currentPbx && currentPbx.state && currentPbx.state !== 'DEACTIVATED' && regionSupportFax) || false
    },
    [regions],
  )

  useAsync(async () => {
    if (dynamicSetupFeature && dynamicSetupFeature.context === selectedPbxId) {
      const useDynamicSetupConfig = dynamicSetupFeature.setting === 'ENABLED'
      if (selectedPbxId && !useDynamicSetupConfig) {
        const gsConfig = await fetchGuidedSetupConfig(selectedPbxId)
        setGuidedSetupConfig(gsConfig)
      } else {
        setGuidedSetupConfig(undefined)
      }

      if (accountKey && useDynamicSetupConfig) {
        const dsConfig = await fetchDynamicSetupConfig(accountKey)
        setDynamicSetupConfig(dsConfig)
      } else {
        setDynamicSetupConfig(undefined)
      }
    } else {
      setDynamicSetupConfig(undefined)
      setGuidedSetupConfig(undefined)
    }
  }, [selectedPbxId, accountKey, dynamicSetupFeature])

  useAsync(async () => {
    setSubscriptions([])

    if (accountKey && licensesDetails && licensesDetails.isLoaded) {
      let billingData: BillingInfo | undefined = undefined
      const hasJiveChannel = licensesDetails.results?.some((license) => license.channel === CHANNEL_JIVE)
      if (!hasJiveChannel) {
        try {
          billingData = await getBillingInfo(accountKey)
          setBillingInfo(billingData)
        } catch (error: any) {
          billingData = {
            country: '',
            currency: '',
            commerceSystems: {},
            status: 'UNAUTHORIZED',
          }
          setBillingInfo(billingData)
        }

        if (billingData?.country) {
          try {
            const migrationData = await getProductMigrationDetails(accountKey)
            setProductMigrationDetails(migrationData)
          } catch (error) {
            setProductMigrationDetails(undefined)
            console.error(error)
          }

          try {
            const subscriptions = await getSubscriptions(accountKey)
            setSubscriptions(subscriptions || [])
          } catch (error) {
            console.error(error)
          }
        }
      }
    }
  }, [accountKey, licensesDetails?.isLoaded])

  useAsync(async () => {
    if (selectedPbxId) {
      await handleFetchPBX(selectedPbxId)
    } else {
      setCurrentPbx(null)
    }
  }, [selectedPbxId])

  useAsync(async () => {
    if (accountKey) {
      const userCount = await getUserCountForMixpanel(accountKey)
      mixpanel.push(['register', { numberOfUsers: userCount }])
      mixpanel.push(['register', { accountKey: accountKey }])
      const account = await fetchAccount(accountKey)
      setUserAccount(account)

      const getUsersList = await fetchUsers({
        filters: { filter: undefined },
        lmiAccountKey: accountKey,
      })
      const userList = await getUsersList()
      const nbUsers = userList?.total || 0
      sessionStorage.setItem('usercount', nbUsers)
    }
  }, [accountKey])

  useAsync(async () => {
    if (selectedPbxId) {
      await Promise.all([fetchPbxSmsCampaignDetails(selectedPbxId), fetchProductsExploration(selectedPbxId)]).then(
        (values) => {
          setPbxSmsCampaignDetails(values[0])
          setProductsExploration(values[1])
        },
      )
    }
  }, [selectedPbxId])

  useMessaging('refreshProductsExplorationData', async () => {
    if (selectedPbxId) {
      const productsExploration = await fetchProductsExploration(selectedPbxId)
      setProductsExploration(productsExploration)
    }
  })

  useMessaging(BILLING_INFO_UPDATE_EVENT, (payload: unknown) => {
    const { billingInfo } = payload as { billingInfo?: BillingInfo }
    billingInfo && setBillingInfo(billingInfo)
  })

  useEffect(() => {
    sessionStorage.setItem('product_list', `${licensesDetails?.products.join(', ')}`)
    storeLicensesUsage(licensesDetails)
  }, [licensesDetails])

  useAsync(async () => {
    if (accountKey) {
      const legalTerms = await fetchAdminCopilotLegalTerms(accountKey)
      setCopilotLegalTerms(legalTerms)
    }
  }, [accountKey])

  useMessaging('updateRegion', (region: unknown) => {
    if (region) {
      setCurrentPbx((pbx) => ({ ...pbx, region: region as string }))
    }
  })

  const hasTemplatableProduct = (products: string[]) => {
    return products.some((product) => templatableProduct.includes(product))
  }

  return (
    <OrganizationContext.Provider
      value={{
        ...currentPbx,
        guidedSetupConfig,
        dynamicSetupConfig,
        pbxSmsCampaignDetails,
        productsExploration,
        copilotLegalTerms,
        onCopilotLegalTermsAgreements,
        useRebranding2021: true,
        showJive: !!(currentPbx && currentPbx.state && currentPbx.state !== 'DEACTIVATED'),
        showVirtualFax: supportVirtualFax(currentPbx),
        showRooms:
          licensesDetails && productsSettings
            ? !!(licensesDetails?.entitlements?.G2M?.roomSystemProvisioned && productsSettings?.G2M?.roomSystemVisible)
            : undefined,
        showAssist: licensesDetails?.products.includes('G2ARS'),
        showWebinar: licensesDetails?.products.includes('G2W'),
        showTraining: licensesDetails?.products.includes('G2T'),
        showMeetings: licensesDetails?.products.includes('G2M'),
        showResolve: licensesDetails?.products.includes('GOTORESOLVE'),
        showHelpdesk: licensesDetails?.products.includes('GOTORESOLVEHELPDESK'),
        showCustomerEngagement:
          licensesDetails?.products.includes('CUSTOMERENGAGEMENT') &&
          features?.find((f) => f.feature === FEATURE_FLAGS.allowCustomerEngagementLicense)?.setting === 'ENABLED',
        showResolveHelpline:
          licensesDetails?.products.includes('GOTORESOLVE') &&
          licensesDetails?.entitlements?.GOTORESOLVE?.callingCardAgentProvisioned === true,
        showResolveKnowledgebase:
          licensesDetails?.products.includes('GOTORESOLVE') &&
          licensesDetails?.entitlements?.GOTORESOLVE?.knowledgeBaseProvisioned === true,
        showSettingsTemplate: hasTemplatableProduct(licensesDetails?.products ?? []),
        showSwitchBackDialog,
        showUpgradeDialog,
        showPbxDeactivatedBanner:
          (licensesDetails &&
            isJiveProductsAvailable(licensesDetails) &&
            currentPbx &&
            currentPbx.state === 'DEACTIVATED') ||
          false,
        setShowSwitchBackDialog,
        setShowUpgradeDialog,
        handleUpgrade,
        licensesDetails,
        billingUserKey: userAccount?.billingUserKey || '',
        hasPaidLicense:
          licensesDetails?.results?.every((element) => element.type !== 'trial') ||
          licensesDetails?.results?.some((element) => element.type === 'paid'),
        billingInfo,
        productMigrationDetails,
        subscriptions,
        commerceSystems: billingInfo?.commerceSystems,
        productsSettings,
        switchFromExternalAdmin: userAccount?.goToAdminTime,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

export default OrganizationProvider
